import { FC, useCallback, useMemo, useState } from "react"

import BigNumber from "bignumber.js"
import { useAtom, useAtomValue } from "jotai"
import Image from "next/image"

import InfoIcon from "@/assets/icons/base64/InfoIcon"
import { selectedMainObligationAtom } from "@/atoms/obligations"
import { SelectedReserveType } from "@/atoms/shared"
import useTrans from "@/hooks/useTrans"
import { formatPercent, formatToken, formatUsd } from "@/utils/numberFormatter"
import { Box, Link, Stack, Typography } from "@mui/material"

import { appSupplyWithdrawPopupState } from "@/atoms/appPopupState"
import { RelendTooltip } from "@/components/relendTooltip"
import { Stat } from "@/components/stat"
import { supplyConfigs } from "@/utils/actions/supply"
import { withdrawConfigs } from "@/utils/actions/withdraw"
import { DEPOSIT, SKELETON_VALUE, WITHDRAW } from "@/utils/constants"
import { StyleObject, TokenSymbol, tokenDecimalDigits } from "@/utils/helpers"
import { RplToken } from "@/utils/modals/RplToken"
import { Configuration, calculateSupplyInterest } from "@/utils/rate"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { USDS } from "@renec-foundation/relend-adapter"
import * as styles from "./styles"

type SupplyWithdrawModalStatProps = {
  selectedReserve: SelectedReserveType
  value: number | undefined
  type: typeof DEPOSIT | typeof WITHDRAW
}

type TitleKeys =
  | "supplyAPY"
  | "yourSupplyLimit"
  | "borrowUtilization"
  | "yourTotalSupply"
  | "yourTotalBorrowLimit"
  | "liquidationThreshold"

type TitleStat = {
  [key in TitleKeys]: React.ReactNode
}

type StatPropsWithKey = {
  titleKey: TitleKeys
  content: React.ReactNode
  contentStyle?: StyleObject
  statsWrapper?: StyleObject
  statsTitleWrapper?: StyleObject
}

const SupplyWithdrawModalStat: FC<SupplyWithdrawModalStatProps> = ({ selectedReserve, value, type }) => {
  const trans = useTrans()
  const popupState = useAtomValue(appSupplyWithdrawPopupState)
  const [selectedObligation] = useAtom(selectedMainObligationAtom)
  const [showMore, setShowMore] = useState(false)

  // configs
  const shouldShowMoreStats = useMemo(() => selectedReserve.symbol !== USDS, [selectedReserve.symbol])
  const valueNonNan = value?.toString() ?? "0"
  const isDeposit = type === DEPOSIT
  const supplyCalculationStats = supplyConfigs.getNewCalculations(selectedObligation, selectedReserve, valueNonNan)
  const withdrawCalculationStats = withdrawConfigs.getNewCalculations(selectedObligation, selectedReserve, valueNonNan)

  const newBorrowLimit = useMemo(
    () => (isDeposit ? supplyCalculationStats.newBorrowLimit : withdrawCalculationStats.newBorrowLimit),
    [isDeposit, supplyCalculationStats.newBorrowLimit, withdrawCalculationStats.newBorrowLimit],
  )

  const newBorrowUtilization = useMemo(
    () => (isDeposit ? supplyCalculationStats.newBorrowUtilization : withdrawCalculationStats.newBorrowUtilization),
    [isDeposit, supplyCalculationStats.newBorrowUtilization, withdrawCalculationStats.newBorrowUtilization],
  )

  const newBorrowLimitDisplay = useMemo(() => {
    if (!newBorrowLimit) return null

    if (newBorrowLimit.isLessThan(0)) {
      return formatUsd(0)
    } else {
      return formatUsd(newBorrowLimit)
    }
  }, [newBorrowLimit])

  const newUtilizationDisplay = useMemo(() => {
    if (!newBorrowUtilization) return null

    if (newBorrowUtilization.isGreaterThanOrEqualTo(1)) {
      return formatPercent(1)
    } else if (newBorrowUtilization.isLessThan(0)) {
      return formatPercent(0)
    } else {
      return formatPercent(newBorrowUtilization)
    }
  }, [newBorrowUtilization])

  const supplyUtilization = useMemo(() => {
    if (!supplyCalculationStats.utilization) return 0

    return supplyCalculationStats.utilization.isLessThanOrEqualTo(1) ? supplyCalculationStats.utilization : 1
  }, [supplyCalculationStats.utilization])

  const withdrawUtilization = useMemo(() => {
    if (!withdrawCalculationStats.utilization) return 0

    return withdrawCalculationStats.utilization.isLessThanOrEqualTo(1) ? withdrawCalculationStats.utilization : 1
  }, [withdrawCalculationStats.utilization])

  const utilization = useMemo(
    () => (isDeposit ? formatPercent(supplyUtilization) : formatPercent(withdrawUtilization)),
    [isDeposit, supplyUtilization, withdrawUtilization],
  )

  const borrowLimit = useMemo(
    () =>
      isDeposit
        ? formatUsd(supplyCalculationStats.borrowLimit ?? 0)
        : formatUsd(withdrawCalculationStats.borrowLimit ?? 0),
    [isDeposit, supplyCalculationStats.borrowLimit, withdrawCalculationStats.borrowLimit],
  )

  const totalTokenSupplied = useMemo(
    () =>
      selectedObligation?.deposits.find((d) => d.reserveAddress === selectedReserve.address)?.amount ||
      new BigNumber(0),
    [selectedObligation?.deposits, selectedReserve.address],
  )

  const totalTokenSuppliedDisplayNumber = useMemo(
    () =>
      isDeposit
        ? totalTokenSupplied.plus(valueNonNan)
        : BigNumber.max(totalTokenSupplied.minus(valueNonNan), new BigNumber(0)),
    [isDeposit, totalTokenSupplied, valueNonNan],
  )

  const valueCurrentPrice = useMemo(
    () => new BigNumber(valueNonNan).multipliedBy(selectedReserve.price),
    [valueNonNan, selectedReserve.price],
  )

  const newLiquidationThreshold = useMemo(() => {
    if (!selectedObligation) return new BigNumber(0)

    return isDeposit
      ? selectedObligation.liquidationThreshold.plus(
          valueCurrentPrice.multipliedBy(selectedObligation.liquidationThresholdFactor),
        )
      : BigNumber.max(
          selectedObligation.liquidationThreshold.minus(
            valueCurrentPrice.multipliedBy(selectedObligation.liquidationThresholdFactor),
          ),
          new BigNumber(0),
        )
  }, [isDeposit, selectedObligation, valueCurrentPrice])

  const tokenDisplayName = useMemo(
    () => RplToken.getTokenDisplayName(selectedReserve.symbol, selectedReserve.name ?? ""),
    [selectedReserve.symbol, selectedReserve.name],
  )

  // config for new supplyAPR calculation
  const newAvailableAmount = useMemo(
    () =>
      popupState.type === DEPOSIT
        ? selectedReserve.availableAmount.plus(value ?? 0)
        : selectedReserve.availableAmount.minus(value ?? 0),
    [popupState.type, selectedReserve.availableAmount, value],
  )

  const config: Configuration = useMemo(
    () => ({
      protocolTakeRate: selectedReserve.interestRateSpread,
      isShowingAPY: true,
      borrowedAmount: selectedReserve.totalBorrow,
      availableAmount: newAvailableAmount.isGreaterThanOrEqualTo(0) ? newAvailableAmount : BigNumber(0.0000001),
      optimalUtilizationRate: selectedReserve.targetUtilization,
      maxUtilizationRate: selectedReserve.maxUtilizationRate,
      minBorrowRate: selectedReserve.minBorrowApr,
      optimalBorrowRate: selectedReserve.targetBorrowApr,
      maxBorrowRate: selectedReserve.maxBorrowApr,
      superMaxBorrowRate: selectedReserve.superMaxBorrowRate,
      accumulatedProtocolFees: selectedReserve.accumulatedProtocolFeesWad,
      decimals: selectedReserve.decimals,
    }),
    [newAvailableAmount, selectedReserve],
  )

  const titleEls: TitleStat = useMemo(
    () => ({
      supplyAPY: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.supply_apy}</Typography>
          <RelendTooltip title={trans.dashboard.modal.supply_apy_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      yourSupplyLimit: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.your_supply_limit}</Typography>
          <RelendTooltip title={trans.dashboard.modal.your_max_supply_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      borrowUtilization: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.borrow_utilization}</Typography>
          <RelendTooltip title={trans.dashboard.modal.borrow_utilization_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      yourTotalSupply: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.your_total_supply}</Typography>
          <RelendTooltip title={trans.dashboard.modal.your_total_supply_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      yourTotalBorrowLimit: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.your_total_borrow_limit}</Typography>
          <RelendTooltip title={trans.dashboard.modal.total_borrow_limit_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
      liquidationThreshold: (
        <>
          <Typography className='text-neutral-1000'>{trans.dashboard.modal.liquidation_threshold}</Typography>
          <RelendTooltip title={trans.dashboard.modal.liquidation_threshold_tooltip}>
            <Image src={InfoIcon} alt='info-icon' width={16} height={16} />
          </RelendTooltip>
        </>
      ),
    }),
    [trans],
  )

  // remove market price
  const stats: StatPropsWithKey[] = useMemo(
    () => [
      {
        titleKey: "supplyAPY",
        content: (
          <Box sx={styles.supplyAPYText}>
            {formatPercent(selectedReserve.supplyAPYInterest)}
            {value ? <>&rarr; {formatPercent(calculateSupplyInterest(config))}</> : null}
          </Box>
        ),
      },
      {
        titleKey: "yourSupplyLimit",
        content: `${formatToken(selectedReserve.reserveSupplyCap, 0)} ${tokenDisplayName}`,
        contentStyle: styles.semiBoldTextEnd,
      },
    ],
    [config, selectedReserve.reserveSupplyCap, selectedReserve.supplyAPYInterest, tokenDisplayName, value],
  )

  const statsMore: StatPropsWithKey[] = useMemo(
    () => [
      {
        titleKey: "borrowUtilization",
        content: (
          <>
            {utilization}
            {Boolean(value) && newBorrowUtilization ? (
              <>
                {" \u2192 "}
                {newUtilizationDisplay}
              </>
            ) : null}
          </>
        ),
        contentStyle: styles.semiBoldTextEnd,
      },
      {
        titleKey: "yourTotalSupply",
        content: (
          <>
            {selectedObligation && (
              <>
                {formatToken(
                  totalTokenSuppliedDisplayNumber,
                  tokenDecimalDigits[selectedReserve.symbol as TokenSymbol],
                )}{" "}
                {tokenDisplayName}{" "}
                <Typography component='span' style={styles.convertedText}>
                  (~{formatUsd(totalTokenSuppliedDisplayNumber.multipliedBy(selectedReserve.minPrice))})
                </Typography>
              </>
            )}
            {!selectedObligation && SKELETON_VALUE}
          </>
        ),
        contentStyle: styles.semiBoldTextEnd,
      },
      {
        titleKey: "yourTotalBorrowLimit",
        content: (
          <>
            {borrowLimit}
            {Boolean(value) && newBorrowLimit ? (
              <>
                {" \u2192 "}
                {newBorrowLimitDisplay}
              </>
            ) : null}
          </>
        ),
        contentStyle: styles.borrowLimitText,
      },
      {
        titleKey: "liquidationThreshold",
        content: selectedObligation
          ? `${formatUsd(selectedObligation.liquidationThreshold.toString())} ${
              value ? `\u2192 ${formatUsd(newLiquidationThreshold)}` : ""
            }`
          : SKELETON_VALUE,
        contentStyle: styles.liquidationThresholdText,
      },
    ],
    [
      borrowLimit,
      newBorrowLimit,
      newBorrowUtilization,
      newLiquidationThreshold,
      selectedObligation,
      selectedReserve,
      tokenDisplayName,
      totalTokenSuppliedDisplayNumber,
      utilization,
      value,
      newBorrowLimitDisplay,
      newUtilizationDisplay,
    ],
  )

  const handleShowMore = useCallback(() => {
    setShowMore((prev) => !prev)
  }, [])

  return (
    <Stack spacing={0} mb={3}>
      {stats.map((stat, index) => (
        <Stat
          key={`${stat.titleKey}-${index}`}
          titleEl={titleEls[stat.titleKey]}
          content={stat.content}
          contentStyle={stat.contentStyle}
        />
      ))}

      {shouldShowMoreStats ? <Box className='border-t-2 border-t-secondary border-dashed my-2' /> : null}
      {shouldShowMoreStats && showMore
        ? statsMore.map((stat, index) => (
            <Stat
              key={`${stat.titleKey}-${index}`}
              titleEl={titleEls[stat.titleKey]}
              content={stat.content}
              contentStyle={stat.contentStyle}
            />
          ))
        : null}

      {shouldShowMoreStats ? (
        <Link
          onClick={handleShowMore}
          data-testid='btn-show-more-less'
          className='text-primary text-xs sm:text-sm m-auto cursor-pointer hover:text-primary/80 mt-1'
        >
          {showMore ? trans.dashboard.modal.show_less : trans.dashboard.modal.show_more}
          {showMore ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
        </Link>
      ) : null}
    </Stack>
  )
}

export default SupplyWithdrawModalStat
